import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Users, ShieldAlert, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { nombre: "", rut: "", email: "" };

export default function Recomendaciones() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [recs, setRecs] = useState([{ ...EMPTY }, { ...EMPTY }, { ...EMPTY }]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/applications/${id}/recommenders`);
      setInfo(data);
      if (data.recommenders?.length === 3) setRecs(data.recommenders.map((r) => ({ ...r })));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/panel");
    }
  };
  useEffect(() => { load(); }, [id]);

  const setField = (i, field, val) => {
    setRecs((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: val } : r)));
  };

  const submit = async () => {
    for (const r of recs) {
      if (!r.nombre.trim() || !r.rut.trim() || !r.email.trim()) {
        toast.error("Completa nombre, RUT y correo de los 3 socios.");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/applications/${id}/recommenders`, { recommenders: recs });
      toast.success("Recomendaciones enviadas. EIM revisará tu ingreso nuevamente.");
      navigate(`/solicitud/${id}/resultado`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (!info) return <AppShell><div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AppShell>;

  if (!info.can_submit) {
    return (
      <AppShell>
        <div className="bg-white rounded-2xl border border-border p-8 text-center max-w-lg mx-auto" data-testid="recommenders-unavailable">
          <CheckCircle2 className="w-10 h-10 text-primary mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">No hay recomendaciones pendientes</h1>
          <p className="text-muted-foreground">Tu solicitud {info.human_id || ""} está en estado "{info.public_status}". No necesitas ingresar recomendaciones ahora.</p>
          <Button onClick={() => navigate(`/solicitud/${id}/resultado`)} className="btn-lift rounded-xl mt-5" data-testid="btn-back-result">Ver estado de mi solicitud</Button>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <ShieldAlert className="w-7 h-7 text-accent" />
          <h1 className="text-2xl font-bold">Socios que te recomiendan</h1>
        </div>
        <p className="text-muted-foreground mb-6">
          Tu ingreso no fue aceptado en primera instancia. Para continuar, indica el nombre, RUT y correo de
          <strong> 3 socios del EIM</strong> que te recomienden. EIM los revisará y tomará una nueva decisión.
        </p>
        {info.entry_comment && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-900" data-testid="entry-comment-note">
            <strong>Motivo indicado por EIM:</strong> {info.entry_comment}
          </div>
        )}

        <div className="space-y-5">
          {recs.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl border border-border p-5" data-testid={`recommender-card-${i}`}>
              <p className="flex items-center gap-2 font-semibold mb-3"><Users className="w-4 h-4 text-primary" /> Socio recomendante {i + 1}</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Nombre completo</Label>
                  <Input className="h-11 rounded-xl mt-1" value={r.nombre} onChange={(e) => setField(i, "nombre", e.target.value)} data-testid={`rec-nombre-${i}`} />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">RUT</Label>
                  <Input className="h-11 rounded-xl mt-1" placeholder="12.345.678-9" value={r.rut} onChange={(e) => setField(i, "rut", e.target.value)} data-testid={`rec-rut-${i}`} />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase text-slate-500">Correo</Label>
                  <Input type="email" className="h-11 rounded-xl mt-1" value={r.email} onChange={(e) => setField(i, "email", e.target.value)} data-testid={`rec-email-${i}`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" onClick={() => navigate(`/solicitud/${id}/resultado`)} className="rounded-xl border-2">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="btn-lift rounded-xl flex-1 h-12" data-testid="btn-submit-recommenders">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : "Enviar recomendaciones"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
